/**
 * @voltxampere/web — Recent command persistence (Sprint 22 C4)
 *
 * Bridges the in-memory `recentCommandIds` signal (Sprint 22 C2) to
 * the `commandRecents` IndexedDB store (DB v2). The palette UI
 * still reads/writes the signal — this module hydrates it at boot
 * and mirrors writes to IDB so the recent ring survives reloads.
 *
 * Failure mode:
 *  - IDB unavailable (private mode, hostile environment) → the
 *    signal still works for the current session; we log and move
 *    on. The next reload starts with an empty Recent section.
 *  - IDB throws on read at boot → signal stays empty; subsequent
 *    pushes still attempt to persist. A persistent failure means
 *    the user simply has no Recent history, which is degraded but
 *    correct.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import {
  clearCommandRecents,
  getCommandRecents,
  pushCommandRecent,
} from "@voltxampere/persistence";

import { recentCommandIds, recordRecent } from "../store/palette.js";

let hydrated = false;

/**
 * Hydrate the recentCommandIds signal from the `commandRecents`
 * store. Called once during boot (`apps/web/src/main.ts`) before
 * the palette is opened. Returns the number of ids loaded.
 */
export async function initRecentCommandIds(): Promise<number> {
  if (hydrated) return recentCommandIds.value.length;
  try {
    const ids = await getCommandRecents();
    recentCommandIds.value = ids;
    hydrated = true;
    return ids.length;
  } catch (err) {
    console.warn("[VXA recent] IDB hydration failed; Recent section starts empty.", err);
    hydrated = true;
    return 0;
  }
}

/**
 * Record a command execution. Updates the in-memory signal
 * synchronously (so the palette repaints immediately) and fires a
 * fire-and-forget IDB push.
 *
 * Errors from the IDB push are logged but never thrown — the
 * palette UX should not be coupled to storage health.
 */
export function recordAndPersistRecent(id: string): void {
  recordRecent(id);
  void pushCommandRecent(id).catch((err: unknown) => {
    console.warn(`[VXA recent] Failed to persist '${id}' to IDB:`, err);
  });
}

/**
 * Wipe the Recent ring — both the signal and IDB. Wired to the
 * AYARLAR "Veri" tab "Tümünü temizle" button (Sprint 21 C5) so
 * clearing all data also resets the palette's Recent section.
 */
export async function clearAllRecentCommands(): Promise<void> {
  recentCommandIds.value = [];
  try {
    await clearCommandRecents();
  } catch (err) {
    console.warn("[VXA recent] IDB clear failed:", err);
  }
}

/** Test-only helper. Resets the hydration flag. */
export function __resetRecentForTests(): void {
  hydrated = false;
  recentCommandIds.value = [];
}
